// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import { getSettingsCollection } from "@vanmeter/App/Store/Selectors/SettingsSelectors";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
        // xng-change
        showLargeImage?: boolean;
    }
>;

function Img(props: ImgProps) {
    const { src, altText, loading, ...otherProps } = props;
    // <XNG-Change>
    const [imageSrc, setImageSrc] = useState<string | undefined>(src);
    const [error, setError] = useState(false);
    const notFoundImage = useAppSelector(state => {
        const { smallDefaultProductImage, mediumDefaultProductImage, largeDefaultProductImage } =
            (getSettingsCollection(state).customSettings || {}) as any;

        return props.showLargeImage
            ? largeDefaultProductImage || mediumDefaultProductImage || smallDefaultProductImage
            : mediumDefaultProductImage || smallDefaultProductImage || largeDefaultProductImage;
    });
    const dimensions = useQueryStringsDimensions(imageSrc);

    const handleNotFoundImage = ({ currentTarget }: any) => {
        setError(true);
        currentTarget.src = notFoundImage;
        setImageSrc(notFoundImage);
    };

    useEffect(() => {
        if (src) {
            setImageSrc(src);
        } else {
            setImageSrc(notFoundImage);
        }
    }, [src]);
    // </xng-change>

    if (!imageSrc && !notFoundImage) {
        return null;
    }
    return (
        <StyledImg
            {...otherProps}
            loading={loading || "lazy"}
            src={dimensions.url}
            alt={altText}
            width={dimensions.width || otherProps.width}
            height={dimensions.height || otherProps.height}
            onError={!error ? handleNotFoundImage : undefined}
            className={otherProps.className}
        />
    );
}

const useQueryStringsDimensions = (src?: string) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            url: src,
            width: "",
            height: "",
        };

        if (queryStrings) {
            const parsedUrl = parseImageUrl(src);
            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }
            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

const StyledImg = styled.img<InjectableCss>`
    ${injectCss}
`;

export default Img;
